/**
 * Simple utility for accessing SQLite storage instances.
 */

import type { WrappedRxStorageInstance } from 'rxdb/plugins/core';

import type { AnyDocument } from '../../types';
import type { RxDBInstance } from '../../types/database';
import type { RxStorageInstanceSQLite } from './storages/storage-sqlite/sqlite-storage-instance';
import type {
  SQLiteDatabaseClass,
  SQLiteInternals,
} from './storages/storage-sqlite/sqlite-types';

class SQLiteStorageNotAvailableError extends Error {
  constructor(collectionName: string) {
    super(`SQLite storage is not available for collection ${collectionName}. This feature requires SQLite storage.`);
    this.name = 'SQLiteStorageNotAvailableError';
  }
}

/**
 * Result of getting an unwrapped SQLite storage instance
 */
export interface SQLiteStorageAccess {
  storageInstance: RxStorageInstanceSQLite<AnyDocument>;
  db: SQLiteDatabaseClass;
  tableName: string;
}


/**
 * Get and unwrap a SQLite storage instance from a collection.
 */
export async function getSQLiteStorageInstance(
  database: RxDBInstance,
  collectionName: string,
): Promise<SQLiteStorageAccess> {
  // claude eslint: collectionName is a dynamic string parameter — no static collection key available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  const collection = (database.collections as any)[collectionName];
  if (!collection) {
    throw new Error(`Collection ${collectionName} not found`);
  }

  const wrappedStorageInstance = collection.storageInstance as WrappedRxStorageInstance<AnyDocument, SQLiteInternals, unknown>;
  const storageInstance = wrappedStorageInstance.originalStorageInstance as RxStorageInstanceSQLite<AnyDocument>;

  if (!storageInstance.internals) {
    throw new SQLiteStorageNotAvailableError(collectionName);
  }

  const internals = storageInstance.internals;
  const db = await internals.databasePromise;
  await internals.indexCreationPromise;

  const tableName = storageInstance.tableName;
  if (!tableName) {
    throw new Error(`Table name not available for collection ${collectionName}`);
  }

  return {
    storageInstance,
    db,
    tableName,
  };
}
